import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import type { WidgetType, WidgetItem, ReportGlobalSettings, ReportPage } from './types';
import {
  Box,
  Typography,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Switch,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  IconButton
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  X,
  Type,
  Heading1,
  BarChart3,
  Image as ImageIcon,
  Minus,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Upload,
  Copy,
  Save,
  Trash2,
  Plus,
  Settings2,
  Loader2,
  Layers,
  ChevronUp,
  ChevronDown,
  LayoutGrid,
  Search,
  Info,
  GripVertical,
  Sliders,
} from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showSuccess, showError } from '../../utils/toast';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      paper: '#111827',
      default: '#111827',
    },
    divider: '#374151',
    primary: {
      main: '#f97316', // orange-500
    }
  },
  typography: {
    fontSize: 12,
  },
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: 12,
          backgroundColor: '#1e293b',
          borderRadius: 6,
        },
        input: {
          padding: '6px 10px',
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#374151',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#4b5563',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#f97316',
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 12,
          color: '#9ca3af',
          '&.Mui-focused': {
            color: '#f97316',
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          padding: '6px 10px',
          fontSize: 12,
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 12,
        }
      }
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          fontSize: 11,
          padding: '3px 8px',
          color: '#9ca3af',
          borderColor: '#374151',
          textTransform: 'none',
          '&.Mui-selected': {
            backgroundColor: '#ea580c',
            color: '#ffffff',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#c2410c',
            }
          }
        }
      }
    }
  }
});

export type CanvaSidebarTab = 'elements' | 'pages' | 'settings';

interface CanvaSidebarProps {
  activeTab: CanvaSidebarTab | null;
  onTabChange: (tab: CanvaSidebarTab | null) => void;
  selectedWidget?: WidgetItem | null;
  globalSettings?: ReportGlobalSettings;
  pages?: ReportPage[];
  activePageId?: string;
  onSelectPage?: (pageId: string) => void;
  onAddPage?: () => void;
  onDeletePage?: (pageId: string) => void;
  onMovePage?: (pageId: string, direction: 'up' | 'down') => void;
  onReorderPages?: (sourceIndex: number, destinationIndex: number) => void;
  onAddWidget: (type: WidgetType) => void;
  onUpdateGlobalSettings?: (updates: Partial<ReportGlobalSettings>) => void;
  onSaveGlobalSettings?: () => void;
  onSyncHeaderToAll?: () => void;
  onSyncFooterToAll?: () => void;
  onUpdateWidget?: (id: string, updates: Partial<WidgetItem['properties']>) => void;
  onDeleteWidget?: (id: string) => void;
  onDeselectWidget?: () => void;
  onOpenChartModal?: (widget?: WidgetItem) => void;
  reportId?: string | number;
}

export interface ElementDefinition {
  type: WidgetType;
  label: string;
  category: 'Typography' | 'Analytics' | 'Media';
  description: string;
  icon: React.ReactNode;
  hint: string;
  color: string;
  iconBg: string;
  hoverBorder: string;
  hoverGlow: string;
}

const ELEMENT_DEFINITIONS: ElementDefinition[] = [
  // Typography & Content
  {
    type: 'heading',
    label: 'Heading Text',
    category: 'Typography',
    description: 'Main section titles & headlines (H1, H2, H3)',
    icon: <Heading1 size={18} />,
    hint: 'H1-H3',
    color: '#f97316',
    iconBg: 'rgba(249, 115, 22, 0.15)',
    hoverBorder: '#f97316',
    hoverGlow: 'rgba(249, 115, 22, 0.25)'
  },
  {
    type: 'text',
    label: 'Body Paragraph',
    category: 'Typography',
    description: 'Rich descriptive narrative and bullet points',
    icon: <Type size={18} />,
    hint: 'Body',
    color: '#38bdf8',
    iconBg: 'rgba(56, 189, 248, 0.15)',
    hoverBorder: '#38bdf8',
    hoverGlow: 'rgba(56, 189, 248, 0.25)'
  },

  // Analytics & Visualizations
  {
    type: 'chart',
    label: 'Dynamic Chart',
    category: 'Analytics',
    description: 'Bar, line, pie, area, donut, gauge & metrics',
    icon: <BarChart3 size={18} />,
    hint: 'Chart',
    color: '#a855f7',
    iconBg: 'rgba(168, 85, 247, 0.15)',
    hoverBorder: '#a855f7',
    hoverGlow: 'rgba(168, 85, 247, 0.25)'
  },

  // Media & Visual Layout
  {
    type: 'image',
    label: 'Image / Diagram',
    category: 'Media',
    description: 'Upload PNG, JPG or SVG architectural diagrams',
    icon: <ImageIcon size={18} />,
    hint: 'Asset',
    color: '#10b981',
    iconBg: 'rgba(16, 185, 129, 0.15)',
    hoverBorder: '#10b981',
    hoverGlow: 'rgba(16, 185, 129, 0.25)'
  },
  {
    type: 'divider',
    label: 'Divider Line',
    category: 'Media',
    description: 'Horizontal separator line between page sections',
    icon: <Minus size={18} />,
    hint: 'Rule',
    color: '#ec4899',
    iconBg: 'rgba(236, 72, 153, 0.15)',
    hoverBorder: '#ec4899',
    hoverGlow: 'rgba(236, 72, 153, 0.25)'
  }
];

const CanvaSidebar: React.FC<CanvaSidebarProps> = ({
  activeTab,
  onTabChange,
  globalSettings,
  pages,
  activePageId,
  onSelectPage,
  onAddPage,
  onDeletePage,
  onMovePage,
  onReorderPages,
  onAddWidget,
  onUpdateGlobalSettings,
  onSaveGlobalSettings,
  onSyncHeaderToAll,
  onSyncFooterToAll,
  onOpenChartModal,
  reportId
}) => {
  const routeParams = useParams<{ id: string }>();
  const activeReportId = reportId || routeParams.id;
  const [elementSearchTerm, setElementSearchTerm] = useState('');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [draggedPageIndex, setDraggedPageIndex] = useState<number | null>(null);
  const [dragOverPageIndex, setDragOverPageIndex] = useState<number | null>(null);


  const gs = globalSettings || {
    reportTitle: '',
    reportSubtitle: '',
    authorName: '',
    pageFormat: 'A4',
    orientation: 'portrait',
    pageMargin: 24,
    backgroundColor: '#ffffff',
    showGridGuides: true,
    enableHeader: true,
    headerProperties: {},
    enableFooter: true,
    footerProperties: {}
  };

  const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showError('Invalid file format. Please upload an image file (PNG, JPG, SVG, WEBP, GIF).');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      showError(`Logo image is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum allowed size is 5MB.`);
      e.target.value = '';
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    try {
      setIsUploadingLogo(true);
      const formData = new FormData();
      formData.append('file', file);

      let uploadedData: any = null;
      if (activeReportId) {
        const res = await baseapi.post(`custom_reports/${activeReportId}/upload-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadedData = res.data?.data || res.data;
      }

      const imageName = uploadedData?.image_name || file.name;
      const originalName = uploadedData?.original_name || file.name;
      const imageKey = uploadedData?.image_key || '';
      const size = uploadedData?.size || file.size;
      const contentType = uploadedData?.content_type || file.type;
      const finalUrl = uploadedData?.url || uploadedData?.image_url || (imageKey ? `/api/${imageKey}` : previewUrl);

      onUpdateGlobalSettings?.({
        headerProperties: {
          ...gs.headerProperties,
          image_name: imageName,
          original_name: originalName,
          image_key: imageKey,
          size: size,
          content_type: contentType,
          image_url: finalUrl
        }
      });

      showSuccess(`Uploaded logo: ${originalName}`);
    } catch (err: any) {
      showError(err.response?.data?.message || err.message || 'Logo upload error, applied local preview');
      onUpdateGlobalSettings?.({
        headerProperties: {
          ...gs.headerProperties,
          image_name: file.name,
          original_name: file.name,
          image_url: previewUrl
        }
      });
    } finally {
      setIsUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleElementClick = (type: WidgetType) => {
    if (type === 'chart') {
      onOpenChartModal?.();
      return;
    }

    onAddWidget(type);
  };



  return (
    <ThemeProvider theme={darkTheme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: activeTab !== null ? 384 : 64,
          flexShrink: 0,
          zIndex: 30,
          bgcolor: '#0f172a',
          borderRight: '1px solid #1f2937',
          transition: 'width 0.15s ease',
          userSelect: 'none'
        }}
      >
        {/* Full-Width Heading Bar at the Top of the Left Sidebar */}
        <Box
          sx={{
            height: 48,
            px: 1.75,
            bgcolor: '#0f172a',
            borderBottom: '1px solid #1f2937',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}
        >
          {activeTab !== null ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {activeTab === 'elements' ? (
                  <LayoutGrid size={16} color="#06b6d4" />
                ) : activeTab === 'pages' ? (
                  <Layers size={16} color="#10b981" />
                ) : (
                  <Settings2 size={16} color="#f59e0b" />
                )}
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 13, textTransform: 'capitalize' }}>
                  {activeTab === 'elements'
                    ? 'Elements Library'
                    : activeTab === 'pages'
                      ? 'Page Manager'
                      : 'Report Settings'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {activeTab === 'settings' && onSaveGlobalSettings && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Save size={11} />}
                    onClick={onSaveGlobalSettings}
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      py: 0.25,
                      px: 1,
                      height: 24,
                      borderRadius: 1,
                      bgcolor: '#f59e0b',
                      boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
                      '&:hover': { bgcolor: '#d97706' }
                    }}
                  >
                    Save
                  </Button>
                )}

                <IconButton
                  size="small"
                  onClick={() => onTabChange(null)}
                  sx={{ color: '#94a3b8', p: 0.5, '&:hover': { color: '#ffffff', bgcolor: 'rgba(255, 255, 255, 0.08)' } }}
                >
                  <X size={16} />
                </IconButton>
              </Box>
            </>
          ) : (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Tooltip title="Expand Elements" placement="right" arrow>
                <IconButton
                  size="small"
                  onClick={() => onTabChange('elements')}
                  sx={{ color: '#94a3b8', p: 0.5, '&:hover': { color: '#ffffff', bgcolor: 'rgba(255, 255, 255, 0.08)' } }}
                >
                  <LayoutGrid size={16} color="#06b6d4" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Below Heading Bar: Left Dock Rail (64px) + Side Content (320px) */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* 1. Left Vertical Dock Rail (64px) */}
          <Box
            sx={{
              width: 64,
              height: '100%',
              bgcolor: '#0f172a',
              borderRight: activeTab !== null ? '1px solid #1e293b' : 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 1.25,
              gap: 0.75,
              userSelect: 'none',
              flexShrink: 0
            }}
          >
            {/* 1. Elements Library Tab - Cyan Theme */}
            <Tooltip title="Elements Library" placement="right" arrow>
              <Box
                onClick={() => onTabChange(activeTab === 'elements' ? null : 'elements')}
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.35,
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  bgcolor: activeTab === 'elements' ? 'rgba(6, 182, 212, 0.18)' : 'transparent',
                  border: activeTab === 'elements' ? '1.5px solid #06b6d4' : '1px solid transparent',
                  color: activeTab === 'elements' ? '#ffffff' : '#94a3b8',
                  boxShadow: activeTab === 'elements' ? '0 0 14px rgba(6, 182, 212, 0.4)' : 'none',
                  '&:hover': {
                    bgcolor: activeTab === 'elements' ? 'rgba(6, 182, 212, 0.25)' : 'rgba(6, 182, 212, 0.1)',
                    color: '#38bdf8',
                    borderColor: activeTab === 'elements' ? '#06b6d4' : 'rgba(6, 182, 212, 0.4)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <Box sx={{ color: activeTab === 'elements' ? '#06b6d4' : 'inherit', display: 'flex', alignItems: 'center' }}>
                  <LayoutGrid size={18} />
                </Box>
                <Typography sx={{ fontSize: 9.5, fontWeight: activeTab === 'elements' ? 700 : 500, lineHeight: 1 }}>
                  Elements
                </Typography>
              </Box>
            </Tooltip>

            {/* Separator */}
            <Divider sx={{ width: 34, borderColor: '#334155', my: 0.5 }} />

            {/* 2. Pages Manager Tab - Emerald Theme */}
            <Tooltip title={`Page Manager (${(pages || []).length} Pages)`} placement="right" arrow>
              <Box
                onClick={() => onTabChange(activeTab === 'pages' ? null : 'pages')}
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.35,
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  bgcolor: activeTab === 'pages' ? 'rgba(16, 185, 129, 0.18)' : 'transparent',
                  border: activeTab === 'pages' ? '1.5px solid #10b981' : '1px solid transparent',
                  color: activeTab === 'pages' ? '#ffffff' : '#94a3b8',
                  boxShadow: activeTab === 'pages' ? '0 0 14px rgba(16, 185, 129, 0.4)' : 'none',
                  position: 'relative',
                  '&:hover': {
                    bgcolor: activeTab === 'pages' ? 'rgba(16, 185, 129, 0.25)' : 'rgba(16, 185, 129, 0.1)',
                    color: '#34d399',
                    borderColor: activeTab === 'pages' ? '#10b981' : 'rgba(16, 185, 129, 0.4)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <Box sx={{ color: activeTab === 'pages' ? '#10b981' : 'inherit', display: 'flex', alignItems: 'center' }}>
                  <Layers size={17} />
                </Box>
                <Typography sx={{ fontSize: 9.5, fontWeight: activeTab === 'pages' ? 700 : 500, lineHeight: 1 }}>
                  Pages
                </Typography>
                {(pages || []).length > 1 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 4,
                      bgcolor: activeTab === 'pages' ? '#10b981' : '#64748b',
                      color: 'white',
                      fontSize: 8.5,
                      fontWeight: 700,
                      borderRadius: '10px',
                      px: 0.5,
                      py: 0.05,
                      lineHeight: 1
                    }}
                  >
                    {(pages || []).length}
                  </Box>
                )}
              </Box>
            </Tooltip>

            {/* 3. Settings Tab - Amber Theme */}
            <Tooltip title="Global Report Settings" placement="right" arrow>
              <Box
                onClick={() => onTabChange(activeTab === 'settings' ? null : 'settings')}
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.35,
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  bgcolor: activeTab === 'settings' ? 'rgba(245, 158, 11, 0.18)' : 'transparent',
                  border: activeTab === 'settings' ? '1.5px solid #f59e0b' : '1px solid transparent',
                  color: activeTab === 'settings' ? '#ffffff' : '#94a3b8',
                  boxShadow: activeTab === 'settings' ? '0 0 14px rgba(245, 158, 11, 0.4)' : 'none',
                  '&:hover': {
                    bgcolor: activeTab === 'settings' ? 'rgba(245, 158, 11, 0.25)' : 'rgba(245, 158, 11, 0.1)',
                    color: '#fbbf24',
                    borderColor: activeTab === 'settings' ? '#f59e0b' : 'rgba(245, 158, 11, 0.4)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <Box sx={{ color: activeTab === 'settings' ? '#f59e0b' : 'inherit', display: 'flex', alignItems: 'center' }}>
                  <Settings2 size={17} />
                </Box>
                <Typography sx={{ fontSize: 9.5, fontWeight: activeTab === 'settings' ? 700 : 500, lineHeight: 1 }}>
                  Settings
                </Typography>
              </Box>
            </Tooltip>
          </Box>

          {/* 2. Side Content Drawer (320px) */}
          {activeTab !== null && (
            <Box
              sx={{
                width: 320,
                height: '100%',
                bgcolor: '#111827',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              {/* ========================================================= */}
              {/* TAB CONTENT 0: ELEMENTS LIBRARY */}
              {/* ========================================================= */}
              {activeTab === 'elements' && (
                <Box sx={{ p: 1.75, display: 'flex', flexDirection: 'column', gap: 1.75, overflowY: 'auto', flex: 1 }}>
                  {/* Search Elements */}
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search elements..."
                    value={elementSearchTerm}
                    onChange={(e) => setElementSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <Search size={14} style={{ marginRight: 8, color: '#94a3b8' }} />,
                      endAdornment: elementSearchTerm ? (
                        <IconButton size="small" onClick={() => setElementSearchTerm('')} sx={{ p: 0.25, color: '#94a3b8' }}>
                          <X size={12} />
                        </IconButton>
                      ) : null,
                      sx: {
                        bgcolor: '#0f172a',
                        fontSize: 11.5,
                        borderRadius: 1.5,
                        '& fieldset': { borderColor: '#334155' },
                        '&:hover fieldset': { borderColor: '#06b6d4' },
                        '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                      }
                    }}
                  />

                  {/* Info notice below search */}
                  <Box sx={{ p: 1, bgcolor: 'rgba(6, 182, 212, 0.08)', borderRadius: 1.5, border: '1px dashed rgba(6, 182, 212, 0.35)', display: 'flex', gap: 0.85, alignItems: 'center' }}>
                    <Info size={13} color="#22d3ee" style={{ flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 9.5, color: '#cbd5e1', lineHeight: 1.35 }}>
                      Click any element to auto-place on active page, or drag it directly onto an exact grid spot.
                    </Typography>
                  </Box>

                  {/* Categorized Elements */}
                  {(['Typography', 'Analytics', 'Media'] as const).map((category) => {
                    const categoryElements = ELEMENT_DEFINITIONS.filter(
                      (el) =>
                        el.category === category &&
                        (elementSearchTerm.trim() === '' ||
                          el.label.toLowerCase().includes(elementSearchTerm.toLowerCase()) ||
                          el.description.toLowerCase().includes(elementSearchTerm.toLowerCase()))
                    );

                    if (categoryElements.length === 0) return null;

                    const categoryLabels: Record<string, { title: string; color: string }> = {
                      Typography: { title: 'Typography & Content', color: '#38bdf8' },
                      Analytics: { title: 'Charts & Analytics', color: '#a855f7' },
                      Media: { title: 'Media & Structure', color: '#10b981' }
                    };

                    const catMeta = categoryLabels[category] || { title: category, color: '#f97316' };

                    return (
                      <Box key={category} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.5 }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: catMeta.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {catMeta.title}
                          </Typography>
                          <Typography sx={{ fontSize: 9, color: '#64748b', fontWeight: 600 }}>
                            {categoryElements.length} {categoryElements.length === 1 ? 'item' : 'items'}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          {categoryElements.map((elem) => (
                            <Box
                              key={elem.type}
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.setData('text/plain', elem.type);
                                e.dataTransfer.effectAllowed = 'copy';
                              }}
                              onClick={() => handleElementClick(elem.type)}
                              sx={{
                                p: 1.2,
                                bgcolor: '#1e293b',
                                borderRadius: 1.5,
                                border: '1px solid #334155',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                overflow: 'hidden',
                                userSelect: 'none',
                                '&:hover': {
                                  bgcolor: '#243044',
                                  borderColor: elem.color,
                                  transform: 'translateY(-1.5px)',
                                  boxShadow: `0 4px 14px ${elem.hoverGlow}`,
                                  '& .elem-icon-box': {
                                    bgcolor: elem.color,
                                    color: '#ffffff',
                                    transform: 'scale(1.05)',
                                    boxShadow: `0 0 10px ${elem.color}`
                                  },
                                  '& .elem-action-hint': {
                                    color: elem.color,
                                    opacity: 1
                                  }
                                },
                                '&:active': {
                                  transform: 'scale(0.98)',
                                  cursor: 'grabbing'
                                }
                              }}
                            >
                              {/* Icon Box */}
                              <Box
                                className="elem-icon-box"
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 1.25,
                                  bgcolor: elem.iconBg,
                                  color: elem.color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  transition: 'all 0.18s ease'
                                }}
                              >
                                {elem.icon}
                              </Box>

                              {/* Text Metadata */}
                              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#f1f5f9' }}>
                                    {elem.label}
                                  </Typography>
                                  {elem.hint && (
                                    <Typography
                                      sx={{
                                        fontSize: 8.5,
                                        fontWeight: 600,
                                        color: elem.color,
                                        bgcolor: elem.iconBg,
                                        px: 0.6,
                                        py: 0.1,
                                        borderRadius: 0.75,
                                        border: `1px solid ${elem.color}30`
                                      }}
                                    >
                                      {elem.hint}
                                    </Typography>
                                  )}
                                </Box>
                                <Typography sx={{ fontSize: 9.5, color: '#94a3b8', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {elem.description}
                                </Typography>
                                <Typography
                                  className="elem-action-hint"
                                  sx={{
                                    fontSize: 8.5,
                                    color: '#64748b',
                                    fontWeight: 600,
                                    opacity: 0.8,
                                    transition: 'all 0.18s ease'
                                  }}
                                >
                                  Click to add • Drag to canvas
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* ========================================================= */}
              {/* TAB CONTENT 2: REPORT GLOBAL SETTINGS */}
              {/* ========================================================= */}
              {activeTab === 'settings' && (
                <Box sx={{ p: 1.75, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flex: 1 }}>
                  {/* 1. Page & Layout */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ color: '#38bdf8', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Sliders size={13} color="#38bdf8" /> Page & Grid
                    </Typography>

                    <FormControl fullWidth size="small">
                      <InputLabel>Margin</InputLabel>
                      <Select
                        value={gs.pageMargin !== undefined ? gs.pageMargin : 24}
                        label="Margin"
                        onChange={(e) => onUpdateGlobalSettings?.({ pageMargin: Number(e.target.value) })}
                      >
                        <MenuItem value={16}>Compact (16px)</MenuItem>
                        <MenuItem value={24}>Standard (24px)</MenuItem>
                        <MenuItem value={32}>Spacious (32px)</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={gs.showGridGuides !== false}
                          onChange={(e) => onUpdateGlobalSettings?.({ showGridGuides: e.target.checked })}
                          color="primary"
                          size="small"
                        />
                      }
                      label={<Typography sx={{ fontSize: 11 }}>Show Grid Dot Guides</Typography>}
                    />
                  </Box>

                  <Divider />

                  {/* 2. Master Header */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ color: '#f59e0b', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <AlignLeft size={13} color="#f59e0b" /> Master Header
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={gs.enableHeader !== false}
                            onChange={(e) => onUpdateGlobalSettings?.({ enableHeader: e.target.checked })}
                            color="primary"
                            size="small"
                          />
                        }
                        label={<Typography sx={{ fontSize: 10 }}>Enable</Typography>}
                        sx={{ m: 0 }}
                      />
                    </Box>

                    {gs.enableHeader !== false && (
                      <>
                        <TextField
                          label="Header Title"
                          value={gs.headerProperties?.content || ''}
                          onChange={(e) => onUpdateGlobalSettings?.({
                            headerProperties: { ...gs.headerProperties, content: e.target.value }
                          })}
                          placeholder="e.g. Header Title"
                          fullWidth
                          size="small"
                        />

                        <TextField
                          label="Header Subtitle"
                          value={gs.headerProperties?.headerSubtitle || ''}
                          onChange={(e) => onUpdateGlobalSettings?.({
                            headerProperties: { ...gs.headerProperties, headerSubtitle: e.target.value }
                          })}
                          placeholder="e.g. Assessment Subtitle"
                          fullWidth
                          size="small"
                        />

                        {/* Logo Section */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.25, bgcolor: '#1e293b', borderRadius: 1.5, border: '1px solid #334155' }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#f97316', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Header Logo
                          </Typography>

                          {/* Current Logo Preview & Metadata */}
                          {(gs.headerProperties?.image_url || gs.headerProperties?.image_key) && (
                            <Box sx={{ p: 1, bgcolor: '#0f172a', borderRadius: 1, border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                              <Box sx={{ width: '100%', height: 60, bgcolor: '#1e293b', borderRadius: 0.75, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <img
                                  src={gs.headerProperties?.image_url || (gs.headerProperties?.image_key ? `/api/${gs.headerProperties.image_key}` : '')}
                                  alt={gs.headerProperties?.original_name || 'Logo Preview'}
                                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                />
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: '#f1f5f9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
                                  {gs.headerProperties?.original_name || gs.headerProperties?.image_name || 'Header Logo'}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => onUpdateGlobalSettings?.({
                                    headerProperties: {
                                      ...gs.headerProperties,
                                      image_url: '',
                                      image_key: '',
                                      image_name: '',
                                      original_name: ''
                                    }
                                  })}
                                  sx={{ color: '#ef4444', p: 0.25 }}
                                  title="Remove Logo"
                                >
                                  <Trash2 size={13} />
                                </IconButton>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                                {gs.headerProperties?.size && (
                                  <Typography sx={{ fontSize: 9.5, color: '#94a3b8' }}>
                                    {(gs.headerProperties.size / 1024).toFixed(1)} KB
                                  </Typography>
                                )}
                                {gs.headerProperties?.content_type && (
                                  <Typography sx={{ fontSize: 9.5, color: '#38bdf8', bgcolor: 'rgba(56, 189, 248, 0.1)', px: 0.5, py: 0.1, borderRadius: 0.5 }}>
                                    {gs.headerProperties.content_type}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}

                          {/* Upload Button */}
                          <Button
                            variant="contained"
                            size="small"
                            component="label"
                            disabled={isUploadingLogo}
                            startIcon={isUploadingLogo ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                            sx={{
                              fontSize: 11,
                              textTransform: 'none',
                              bgcolor: '#ea580c',
                              '&:hover': { bgcolor: '#c2410c' },
                              py: 0.6
                            }}
                          >
                            {isUploadingLogo ? 'Uploading Logo...' : (gs.headerProperties?.image_url ? 'Replace Logo' : 'Upload Logo File')}
                            <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
                          </Button>

                          {/* Logo Alignment & Height */}
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Logo Align</InputLabel>
                              <Select
                                value={gs.headerProperties?.logoAlign || 'left'}
                                label="Logo Align"
                                onChange={(e) => onUpdateGlobalSettings?.({
                                  headerProperties: { ...gs.headerProperties, logoAlign: e.target.value as any }
                                })}
                              >
                                <MenuItem value="left">Left</MenuItem>
                                <MenuItem value="center">Center</MenuItem>
                                <MenuItem value="right">Right</MenuItem>
                                <MenuItem value="none">Hidden</MenuItem>
                              </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                              <InputLabel>Height</InputLabel>
                              <Select
                                value={gs.headerProperties?.logoHeight || 28}
                                label="Height"
                                onChange={(e) => onUpdateGlobalSettings?.({
                                  headerProperties: { ...gs.headerProperties, logoHeight: Number(e.target.value) }
                                })}
                              >
                                <MenuItem value={20}>20px</MenuItem>
                                <MenuItem value={28}>28px</MenuItem>
                                <MenuItem value={36}>36px</MenuItem>
                                <MenuItem value={48}>48px</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                        </Box>

                        {/* Header Text Alignment */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Text Align:</Typography>
                          <ToggleButtonGroup
                            value={gs.headerProperties?.textAlign || 'left'}
                            exclusive
                            onChange={(_, val) => val && onUpdateGlobalSettings?.({
                              headerProperties: { ...gs.headerProperties, textAlign: val }
                            })}
                            size="small"
                          >
                            <ToggleButton value="left"><AlignLeft size={13} /></ToggleButton>
                            <ToggleButton value="center"><AlignCenter size={13} /></ToggleButton>
                            <ToggleButton value="right"><AlignRight size={13} /></ToggleButton>
                          </ToggleButtonGroup>
                        </Box>

                        {/* Header Border Style */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Divider</InputLabel>
                            <Select
                              value={gs.headerProperties?.dividerStyle || 'solid'}
                              label="Divider"
                              onChange={(e) => onUpdateGlobalSettings?.({
                                headerProperties: { ...gs.headerProperties, dividerStyle: e.target.value as any }
                              })}
                            >
                              <MenuItem value="solid">Solid</MenuItem>
                              <MenuItem value="dashed">Dashed</MenuItem>
                              <MenuItem value="dotted">Dotted</MenuItem>
                              <MenuItem value="none">None</MenuItem>
                            </Select>
                          </FormControl>

                          <FormControl fullWidth size="small">
                            <InputLabel>Border</InputLabel>
                            <Select
                              value={gs.headerProperties?.borderWidth !== undefined ? gs.headerProperties.borderWidth : 1}
                              label="Border"
                              onChange={(e) => onUpdateGlobalSettings?.({
                                headerProperties: { ...gs.headerProperties, borderWidth: Number(e.target.value) }
                              })}
                            >
                              <MenuItem value={0}>0px</MenuItem>
                              <MenuItem value={1}>1px</MenuItem>
                              <MenuItem value={2}>2px</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        <FormControlLabel
                          control={
                            <Switch
                              checked={gs.headerProperties?.skipOnFirstPage || false}
                              onChange={(e) => onUpdateGlobalSettings?.({
                                headerProperties: { ...gs.headerProperties, skipOnFirstPage: e.target.checked }
                              })}
                              color="primary"
                              size="small"
                            />
                          }
                          label={<Typography sx={{ fontSize: 11, color: '#e2e8f0', fontWeight: 500 }}>Skip on First Page (Cover)</Typography>}
                          sx={{ m: 0, color: '#e2e8f0' }}
                        />

                        {onSyncHeaderToAll && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Copy size={12} />}
                            onClick={onSyncHeaderToAll}
                            sx={{ fontSize: 10.5, textTransform: 'none', height: 26, color: '#f59e0b', borderColor: 'rgba(245, 158, 11, 0.4)', '&:hover': { borderColor: '#f59e0b', bgcolor: 'rgba(245, 158, 11, 0.08)' } }}
                          >
                            Sync Header to All Pages
                          </Button>
                        )}
                      </>
                    )}
                  </Box>

                  <Divider />

                  {/* 3. Master Footer */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ color: '#a855f7', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <AlignRight size={13} color="#a855f7" /> Master Footer
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={gs.enableFooter !== false}
                            onChange={(e) => onUpdateGlobalSettings?.({ enableFooter: e.target.checked })}
                            color="primary"
                            size="small"
                          />
                        }
                        label={<Typography sx={{ fontSize: 10 }}>Enable</Typography>}
                        sx={{ m: 0 }}
                      />
                    </Box>

                    {gs.enableFooter !== false && (
                      <>
                        <TextField
                          label="Footer Text / Notice"
                          value={gs.footerProperties?.content || ''}
                          onChange={(e) => onUpdateGlobalSettings?.({
                            footerProperties: { ...gs.footerProperties, content: e.target.value }
                          })}
                          placeholder="e.g. Confidential Report"
                          fullWidth
                          size="small"
                        />

                        {/* Footer Text Alignment */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Text Align:</Typography>
                          <ToggleButtonGroup
                            value={gs.footerProperties?.textAlign || 'left'}
                            exclusive
                            onChange={(_, val) => val && onUpdateGlobalSettings?.({
                              footerProperties: { ...gs.footerProperties, textAlign: val }
                            })}
                            size="small"
                          >
                            <ToggleButton value="left"><AlignLeft size={13} /></ToggleButton>
                            <ToggleButton value="center"><AlignCenter size={13} /></ToggleButton>
                            <ToggleButton value="right"><AlignRight size={13} /></ToggleButton>
                          </ToggleButtonGroup>
                        </Box>

                        {/* Pagination Config */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Pagination</InputLabel>
                            <Select
                              value={gs.footerProperties?.paginationAlign || 'right'}
                              label="Pagination"
                              onChange={(e) => onUpdateGlobalSettings?.({
                                footerProperties: { ...gs.footerProperties, paginationAlign: e.target.value as any }
                              })}
                            >
                              <MenuItem value="left">Left</MenuItem>
                              <MenuItem value="center">Center</MenuItem>
                              <MenuItem value="right">Right</MenuItem>
                              <MenuItem value="none">Hidden</MenuItem>
                            </Select>
                          </FormControl>

                          <FormControl fullWidth size="small">
                            <InputLabel>Format</InputLabel>
                            <Select
                              value={gs.footerProperties?.paginationFormat || 'page_of_total'}
                              label="Format"
                              onChange={(e) => onUpdateGlobalSettings?.({
                                footerProperties: { ...gs.footerProperties, paginationFormat: e.target.value as any }
                              })}
                            >
                              <MenuItem value="page_of_total">Page X of Y</MenuItem>
                              <MenuItem value="slash">X / Y</MenuItem>
                              <MenuItem value="page_only">Page X</MenuItem>
                              <MenuItem value="number_only">X</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        {onSyncFooterToAll && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Copy size={12} />}
                            onClick={onSyncFooterToAll}
                            sx={{ fontSize: 10.5, textTransform: 'none', height: 26, color: '#a855f7', borderColor: 'rgba(168, 85, 247, 0.4)', '&:hover': { borderColor: '#a855f7', bgcolor: 'rgba(168, 85, 247, 0.08)' } }}
                          >
                            Sync Footer to All Pages
                          </Button>
                        )}
                      </>
                    )}
                  </Box>

                  <Divider />

                  {/* 4. Watermark Settings */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ color: '#ec4899', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Type size={13} color="#ec4899" /> Watermark
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(gs.enableWatermark || gs.watermarkSettings?.enabled)}
                            onChange={(e) => onUpdateGlobalSettings?.({
                              enableWatermark: e.target.checked,
                              watermarkSettings: {
                                ...gs.watermarkSettings,
                                enabled: e.target.checked,
                                text: gs.watermarkSettings?.text || '',
                                type: gs.watermarkSettings?.type || 'text',
                                fontSize: gs.watermarkSettings?.fontSize || 54,
                                color: gs.watermarkSettings?.color || '#94a3b8',
                                opacity: gs.watermarkSettings?.opacity !== undefined ? gs.watermarkSettings.opacity : 0.12,
                                rotation: gs.watermarkSettings?.rotation !== undefined ? gs.watermarkSettings.rotation : -45
                              }
                            })}
                            color="primary"
                            size="small"
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                    </Box>

                    {Boolean(gs.enableWatermark || gs.watermarkSettings?.enabled) && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 0.5, p: 1.25, bgcolor: '#1e293b', borderRadius: '8px', border: '1px solid #334155' }}>
                        <TextField
                          label="Watermark Text"
                          placeholder="e.g. CONFIDENTIAL / DRAFT"
                          value={gs.watermarkSettings?.text !== undefined ? gs.watermarkSettings.text : ''}
                          onChange={(e) => onUpdateGlobalSettings?.({
                            enableWatermark: true,
                            watermarkSettings: {
                              ...gs.watermarkSettings,
                              enabled: true,
                              type: 'text',
                              text: e.target.value
                            }
                          })}
                          fullWidth
                          size="small"
                        />

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            label="Font Size (px)"
                            type="number"
                            value={gs.watermarkSettings?.fontSize || 54}
                            onChange={(e) => onUpdateGlobalSettings?.({
                              watermarkSettings: {
                                ...gs.watermarkSettings,
                                fontSize: Number(e.target.value)
                              }
                            })}
                            fullWidth
                            size="small"
                          />
                          <TextField
                            label="Color"
                            type="color"
                            value={gs.watermarkSettings?.color || '#94a3b8'}
                            onChange={(e) => onUpdateGlobalSettings?.({
                              watermarkSettings: {
                                ...gs.watermarkSettings,
                                color: e.target.value
                              }
                            })}
                            fullWidth
                            size="small"
                            sx={{ input: { height: 20, cursor: 'pointer' } }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* ========================================================= */}
              {/* TAB CONTENT 3: PAGE THUMBNAIL MANAGER */}
              {/* ========================================================= */}
              {activeTab === 'pages' && (
                <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25, overflowY: 'auto', flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.25 }}>
                    <Typography sx={{ color: '#10b981', fontWeight: 700, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Pages ({(pages || []).length})
                    </Typography>
                    <Typography sx={{ fontSize: 9.5, color: '#64748b', fontStyle: 'italic' }}>
                      Drag to reorder
                    </Typography>
                  </Box>

                  {/* Add Page Button */}
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={<Plus size={13} />}
                    onClick={onAddPage}
                    sx={{
                      height: 32,
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: 'none',
                      borderColor: 'rgba(16, 185, 129, 0.4)',
                      borderStyle: 'dashed',
                      color: '#34d399',
                      bgcolor: 'rgba(16, 185, 129, 0.08)',
                      borderRadius: 1.5,
                      '&:hover': {
                        borderColor: '#10b981',
                        bgcolor: 'rgba(16, 185, 129, 0.18)',
                        color: '#ffffff'
                      }
                    }}
                  >
                    + Add New Page
                  </Button>

                  {/* Page Cards List */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {(pages || []).map((page, index) => {
                      const isActive = page.id === activePageId;
                      const isDragging = draggedPageIndex === index;
                      const isDragOver = dragOverPageIndex === index && draggedPageIndex !== index;
                      const widgetCount = (page.widgets || []).filter(w => w && w.type !== 'header' && w.type !== 'footer').length;
                      const hasHeader = (page.widgets || []).some(w => w && w.type === 'header');
                      const hasFooter = (page.widgets || []).some(w => w && w.type === 'footer');

                      return (
                        <Box
                          key={page.id}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', String(index));
                            e.dataTransfer.effectAllowed = 'move';
                            setDraggedPageIndex(index);
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            if (dragOverPageIndex !== index) {
                              setDragOverPageIndex(index);
                            }
                          }}
                          onDragLeave={() => {
                            if (dragOverPageIndex === index) {
                              setDragOverPageIndex(null);
                            }
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (draggedPageIndex !== null && draggedPageIndex !== index) {
                              onReorderPages?.(draggedPageIndex, index);
                            }
                            setDraggedPageIndex(null);
                            setDragOverPageIndex(null);
                          }}
                          onDragEnd={() => {
                            setDraggedPageIndex(null);
                            setDragOverPageIndex(null);
                          }}
                          onClick={() => onSelectPage?.(page.id)}
                          sx={{
                            py: 0.75,
                            px: 1,
                            height: 52,
                            borderRadius: 1.5,
                            bgcolor: isActive ? 'rgba(16, 185, 129, 0.12)' : (isDragOver ? '#1e293b' : '#0f172a'),
                            border: isActive
                              ? '1.5px solid #10b981'
                              : (isDragOver ? '1.5px dashed #10b981' : '1px solid #1e293b'),
                            boxShadow: isActive ? '0 0 12px rgba(16, 185, 129, 0.25)' : 'none',
                            opacity: isDragging ? 0.4 : 1,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.85,
                            transition: 'all 0.15s ease',
                            userSelect: 'none',
                            '&:hover': {
                              borderColor: isActive ? '#10b981' : '#475569',
                              bgcolor: isActive ? 'rgba(16, 185, 129, 0.18)' : '#1e293b',
                              transform: isDragging ? 'none' : 'translateY(-1px)'
                            }
                          }}
                        >
                          {/* Drag Handle */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: isActive ? '#34d399' : '#475569',
                              cursor: 'grab',
                              '&:hover': { color: '#94a3b8' },
                              '&:active': { cursor: 'grabbing' }
                            }}
                          >
                            <GripVertical size={13} />
                          </Box>

                          {/* Mini A4 Wireframe Thumbnail */}
                          <Box
                            sx={{
                              width: 24,
                              height: 34,
                              bgcolor: '#ffffff',
                              borderRadius: '2px',
                              border: isActive ? '1px solid #10b981' : '1px solid #cbd5e1',
                              p: '2px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              flexShrink: 0,
                              boxShadow: isActive ? '0 0 6px rgba(16, 185, 129, 0.3)' : '0 1px 2px rgba(0, 0, 0, 0.12)'
                            }}
                          >
                            {/* Mini Header Line */}
                            <Box sx={{ width: '100%', height: 2, bgcolor: hasHeader ? (isActive ? '#10b981' : '#f97316') : '#e2e8f0', borderRadius: '1px' }} />

                            {/* Mini Content Lines */}
                            <Box sx={{ flex: 1, my: '2px', display: 'flex', flexDirection: 'column', gap: '1.5px', justifyContent: 'center' }}>
                              <Box sx={{ width: '85%', height: 1.5, bgcolor: widgetCount > 0 ? (isActive ? '#34d399' : '#f97316') : '#e2e8f0', borderRadius: '1px' }} />
                              <Box sx={{ width: '60%', height: 1.5, bgcolor: widgetCount > 1 ? '#64748b' : '#e2e8f0', borderRadius: '1px' }} />
                            </Box>

                            {/* Mini Footer Line */}
                            <Box sx={{ width: '100%', height: 1.5, bgcolor: hasFooter ? '#64748b' : '#f1f5f9', borderRadius: '1px' }} />
                          </Box>

                          {/* Page Label & Widget Count */}
                          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.15 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Typography sx={{ fontSize: 11, fontWeight: isActive ? 700 : 600, color: isActive ? '#ffffff' : '#e2e8f0', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                                Page {index + 1}
                              </Typography>
                              {isActive && (
                                <Box
                                  sx={{
                                    fontSize: 8.5,
                                    fontWeight: 700,
                                    color: '#34d399',
                                    bgcolor: 'rgba(16, 185, 129, 0.2)',
                                    border: '1px solid rgba(16, 185, 129, 0.4)',
                                    borderRadius: '4px',
                                    px: 0.5,
                                    py: 0.05,
                                    lineHeight: 1
                                  }}
                                >
                                  ACTIVE
                                </Box>
                              )}
                            </Box>
                            <Typography sx={{ fontSize: 9.5, color: isActive ? '#6ee7b7' : '#64748b', lineHeight: 1.1 }}>
                              {widgetCount} {widgetCount === 1 ? 'element' : 'elements'}
                            </Typography>
                          </Box>

                          {/* Quick Up / Down / Delete Toolbar */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15, flexShrink: 0 }}>
                            <Tooltip title="Move Up">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={index === 0}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onMovePage?.(page.id, 'up');
                                  }}
                                  sx={{
                                    p: 0.35,
                                    color: '#94a3b8',
                                    '&:hover': { color: '#ffffff', bgcolor: 'rgba(255, 255, 255, 0.08)' },
                                    '&.Mui-disabled': { color: '#334155' }
                                  }}
                                >
                                  <ChevronUp size={13} />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="Move Down">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={index === (pages || []).length - 1}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onMovePage?.(page.id, 'down');
                                  }}
                                  sx={{
                                    p: 0.35,
                                    color: '#94a3b8',
                                    '&:hover': { color: '#ffffff', bgcolor: 'rgba(255, 255, 255, 0.08)' },
                                    '&.Mui-disabled': { color: '#334155' }
                                  }}
                                >
                                  <ChevronDown size={13} />
                                </IconButton>
                              </span>
                            </Tooltip>

                            {(pages || []).length > 1 && (
                              <Tooltip title="Delete Page">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDeletePage?.(page.id);
                                  }}
                                  sx={{
                                    p: 0.35,
                                    color: '#94a3b8',
                                    '&:hover': { color: '#f87171', bgcolor: 'rgba(239, 68, 68, 0.15)' }
                                  }}
                                >
                                  <Trash2 size={13} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

            </Box>
          )}

        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default CanvaSidebar;
